import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  AppBar, 
  Toolbar, 
  Box, 
  Typography, 
  Button, 
  Container, 
  useScrollTrigger,
  Slide,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useMediaQuery,
  useTheme,
  Divider,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon
} from '@mui/material';
import { APP_ROUTES } from '../../utils/constants';
import { Menu as MenuIcon, KeyboardArrowDown, Dashboard, Person, ExitToApp, Settings } from '@mui/icons-material';
import { useAuth } from '../../hooks/useAuth';
import { getInitials } from '../../utils/helpers';

/**
 * Enhanced Material UI Navbar component with corporate design
 */
const MuiNavbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [solutionsAnchor, setSolutionsAnchor] = useState(null);
  const { user, isAuthenticated, logout } = useAuth();
  
  // Hide navbar on scroll down, show on scroll up
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 100,
  });

  // Detect scroll to add shadow and background opacity
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [scrolled]);
  
  const handleNavigation = (path) => {
    navigate(path);
    if (drawerOpen) {
      setDrawerOpen(false);
    }
    setUserMenuAnchor(null);
  };
  
  const isActive = (path) => {
    return location.pathname === path;
  };
  
  const handleUserMenuOpen = (event) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleLogout = async () => {
    setUserMenuAnchor(null);
    await logout();
    navigate(APP_ROUTES.HOME);
  };

  const handleSolutionsOpen = (event) => {
    setSolutionsAnchor(event.currentTarget);
  };
  const handleSolutionsClose = () => setSolutionsAnchor(null);

  const getDashboardLink = () => {
    if (!user) return APP_ROUTES.HOME;
    
    switch(user.role) {
      case 'system_admin':
        return '/admin';
      case 'school_admin':
        return '/school';
      case 'teacher':
        return '/teacher';
      case 'parent':
        return '/parent';
      case 'mentor':
        return '/mentor';
      default:
        return '/dashboard';
    }
  };
  
  const menuItems = [
    { label: 'Home', path: APP_ROUTES.HOME },
    { label: 'About', path: APP_ROUTES.ABOUT },
    { 
      label: 'Our Solutions', 
      path: APP_ROUTES.SOLUTIONS,
      children: [
        { label: 'Tech Integration', path: APP_ROUTES.SOLUTIONS_TECH_INTEGRATION },
        { label: 'Trainings', path: APP_ROUTES.SOLUTIONS_TRAININGS },
        { label: 'Parental Engagement', path: APP_ROUTES.SOLUTIONS_PARENTAL_ENGAGEMENT },
      ]
    },
    { label: 'Contact', path: APP_ROUTES.CONTACT },
  ];
  
  const MobileDrawer = () => (
    <Drawer
      anchor="right"
      open={drawerOpen}
      onClose={() => setDrawerOpen(false)}
    >
      <Box
        sx={{
          width: 280,
          pt: 2,
          pb: 3,
          display: 'flex',
          flexDirection: 'column',
          height: '100%'
        }}
        role="presentation"
      >
        {/* Logo and branding */}
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', mb: 1 }}>
          <img
            src="https://res.cloudinary.com/digitaliquids-cloud-technologies/image/upload/2_xwp1oc.png"
            alt="Logo"
            style={{ height: 32 }}
          />
          <Typography variant="h6" sx={{ ml: 1, fontWeight: 'bold' }}></Typography>
        </Box>
        
        {/* User info if logged in */}
        {isAuthenticated && (
          <Box sx={{ px: 2, py: 2, bgcolor: 'rgba(0, 0, 0, 0.02)', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Avatar 
                src={user?.profileImage || user?.profilePicture} 
                alt={user?.name || "User"} 
                sx={{ 
                  width: 40, 
                  height: 40,
                  bgcolor: theme.palette.primary.main
                }}
              >
                {(!user?.profileImage && !user?.profilePicture) && getInitials(user?.name || "User")}
              </Avatar>
              <Box sx={{ ml: 1.5 }}>
                <Typography variant="subtitle2">{user?.name || "User"}</Typography>
                <Typography variant="caption" color="text.secondary">{user?.email}</Typography>
              </Box>
            </Box>
          </Box>
        )}
        
        <Divider sx={{ mb: 2 }} />
        
        <List sx={{ flexGrow: 1 }}>
          {menuItems.map((item) => (
            <React.Fragment key={item.path}>
              <ListItem 
                button 
                onClick={() => handleNavigation(item.path)}
                sx={{ 
                  py: 1.5,
                  bgcolor: isActive(item.path) ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
                  '&:hover': {
                    bgcolor: 'rgba(0, 0, 0, 0.08)'
                  }
                }}
              >
                <ListItemText 
                  primary={item.label} 
                  primaryTypographyProps={{ 
                    fontWeight: isActive(item.path) ? 'bold' : 'regular'
                  }}
                />
              </ListItem>
              {item.children && item.children.map((child) => (
                <ListItem
                  button
                  key={child.path}
                  onClick={() => handleNavigation(child.path)}
                  sx={{ pl: 4, py: 1 }}
                >
                  <ListItemText primary={child.label} />
                </ListItem>
              ))}
            </React.Fragment>
          ))}
          
          {/* Add dashboard link when logged in */}
          {isAuthenticated && (
            <ListItem 
              button 
              onClick={() => handleNavigation(getDashboardLink())}
              sx={{ 
                py: 1.5,
                bgcolor: isActive(getDashboardLink()) ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
                '&:hover': {
                  bgcolor: 'rgba(0, 0, 0, 0.08)'
                }
              }}
            >
              <ListItemIcon>
                <Dashboard />
              </ListItemIcon>
              <ListItemText 
                primary="Dashboard" 
                primaryTypographyProps={{ 
                  fontWeight: isActive(getDashboardLink()) ? 'bold' : 'regular'
                }}
              />
            </ListItem>
          )}
        </List>
        
        <Divider sx={{ mt: 2, mb: 2 }} />
        
        <Box sx={{ px: 2 }}>
          {isAuthenticated ? (
            <>
              <Button 
                fullWidth
                variant="outlined" 
                color="primary" 
                sx={{ mb: 1 }}
                onClick={() => handleNavigation(`${getDashboardLink()}/profile`)}
                startIcon={<Person />}
              >
                Profile
              </Button>
              <Button 
                fullWidth
                variant="contained" 
                color="primary"
                onClick={handleLogout}
                startIcon={<ExitToApp />}
              >
                Logout
              </Button>
            </>
          ) : (
            <>
              <Button 
                fullWidth
                variant="outlined" 
                color="primary" 
                sx={{ mb: 1 }}
                onClick={() => handleNavigation(APP_ROUTES.LOGIN)}
              >
                Login
              </Button>
              <Button 
                fullWidth
                variant="contained" 
                color="primary"
                onClick={() => handleNavigation(APP_ROUTES.REGISTER)}
              >
                Sign up
              </Button>
            </>
          )}
        </Box>
      </Box>
    </Drawer>
  );
  
  return (
    <>
      <Slide appear={false} direction="down" in={!trigger}>
        <AppBar 
          position="sticky" 
          color="default" 
          elevation={scrolled ? 4 : 0}
          sx={{ 
            bgcolor: scrolled ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 1)',
            backdropFilter: 'blur(8px)',
            transition: theme.transitions.create(['background-color', 'box-shadow'], {
              duration: theme.transitions.duration.standard,
            }),
            borderBottom: scrolled ? 0 : `1px solid ${theme.palette.divider}`
          }}
        >
          <Container maxWidth="lg">
            <Toolbar disableGutters sx={{ py: 1 }}>
              {/* Logo */}
              <Box 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  mr: 4, 
                  cursor: 'pointer',
                  '&:hover': {
                    opacity: 0.9
                  }
                }}
                onClick={() => handleNavigation(APP_ROUTES.HOME)}
              >
                <img
                  src="https://res.cloudinary.com/digitaliquids-cloud-technologies/image/upload/2_xwp1oc.png"
                  alt="Logo"
                  style={{ height: 40 }}
                />
                <Typography 
                  variant="h6" 
                  component="div" 
                  sx={{ 
                    ml: 1, 
                    fontWeight: 'bold',
                    display: { xs: 'none', sm: 'block' },
                    color: theme.palette.primary.main
                  }}
                >
                  
                </Typography>
              </Box>
              
              {/* Desktop Navigation */}
              {!isMobile && (
                <Box sx={{ flexGrow: 1, display: 'flex' }}>
                  {menuItems.map((item) => {
                    if (!item.children) {
                      return (
                        <Button 
                          key={item.path}
                          onClick={() => handleNavigation(item.path)}
                          color="inherit"
                          sx={{ 
                            mx: 1, 
                            borderRadius: 1,
                            position: 'relative',
                            fontWeight: isActive(item.path) ? 'bold' : 'medium',
                            '&::after': {
                              content: '""',
                              position: 'absolute',
                              width: isActive(item.path) ? '70%' : '0%',
                              height: '3px',
                              bottom: 6,
                              left: '15%',
                              bgcolor: 'primary.main',
                              transition: 'width 0.2s ease'
                            },
                            '&:hover::after': {
                              width: '70%'
                            }
                          }}
                        >
                          {item.label}
                        </Button>
                      );
                    }
                    return (
                      <Box key={item.path} sx={{ mx: 1 }} onMouseLeave={handleSolutionsClose}>
                        <Button
                          color="inherit"
                          onMouseEnter={handleSolutionsOpen}
                          onClick={() => handleNavigation(item.path)}
                          endIcon={<KeyboardArrowDown fontSize="small" />}
                          sx={{ 
                            borderRadius: 1,
                            position: 'relative',
                            fontWeight: location.pathname.startsWith(APP_ROUTES.SOLUTIONS) ? 'bold' : 'medium'
                          }}
                        >
                          {item.label}
                        </Button>
                        <Menu
                          anchorEl={solutionsAnchor}
                          open={Boolean(solutionsAnchor)}
                          onClose={handleSolutionsClose}
                          MenuListProps={{ onMouseLeave: handleSolutionsClose }}
                          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                       >
                          {item.children.map((child) => (
                            <MenuItem key={child.path} onClick={() => { handleSolutionsClose(); handleNavigation(child.path); }}>
                              {child.label}
                            </MenuItem>
                          ))}
                        </Menu>
                      </Box>
                    );
                  })}
                </Box>
              )}
              
              {/* Authentication Buttons or User Avatar - Desktop */}
              {!isMobile && (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {isAuthenticated ? (
                    <>
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center',
                          cursor: 'pointer',
                          '&:hover': { opacity: 0.8 }
                        }}
                        onClick={handleUserMenuOpen}
                      >
                        <Avatar 
                          src={user?.profileImage || user?.profilePicture} 
                          alt={user?.name || "User"} 
                          sx={{ 
                            width: 40, 
                            height: 40,
                            bgcolor: theme.palette.primary.main
                          }}
                        >
                          {(!user?.profileImage && !user?.profilePicture) && getInitials(user?.name || "User")}
                        </Avatar>
                        <KeyboardArrowDown 
                          fontSize="small" 
                          sx={{ ml: 0.5, color: 'text.secondary' }} 
                        />
                      </Box>
                      <Menu
                        anchorEl={userMenuAnchor}
                        open={Boolean(userMenuAnchor)}
                        onClose={handleUserMenuClose}
                        PaperProps={{
                          elevation: 3,
                          sx: { minWidth: 220, mt: 1.5 }
                        }}
                        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                      >
                        <MenuItem 
                          dense 
                          onClick={handleUserMenuClose} 
                          sx={{ 
                            pointerEvents: 'none', 
                            opacity: 0.7,
                            py: 1,
                            px: 2
                          }}
                        >
                          <Typography variant="body2" noWrap>
                            Signed in as <strong>{user?.name || user?.email}</strong>
                          </Typography>
                        </MenuItem>
                        <Divider sx={{ my: 1 }} />
                        <MenuItem onClick={() => handleNavigation(getDashboardLink())} sx={{ py: 1.25, px: 2 }}>
                          <ListItemIcon>
                            <Dashboard fontSize="small" />
                          </ListItemIcon>
                          Dashboard
                        </MenuItem>
                        <MenuItem onClick={() => handleNavigation(`${getDashboardLink()}/profile`)} sx={{ py: 1.25, px: 2 }}>
                          <ListItemIcon>
                            <Person fontSize="small" />
                          </ListItemIcon>
                          Profile
                        </MenuItem>
                        <MenuItem onClick={() => handleNavigation(`${getDashboardLink()}/settings`)} sx={{ py: 1.25, px: 2 }}>
                          <ListItemIcon>
                            <Settings fontSize="small" />
                          </ListItemIcon>
                          Settings
                        </MenuItem>
                        <Divider sx={{ my: 1 }} />
                        <MenuItem onClick={handleLogout} sx={{ py: 1.25, px: 2 }}>
                          <ListItemIcon>
                            <ExitToApp fontSize="small" />
                          </ListItemIcon>
                          Logout
                        </MenuItem>
                      </Menu>
                    </>
                  ) : (
                    <>
                      <Button 
                        variant="text" 
                        color="inherit"
                        onClick={() => handleNavigation(APP_ROUTES.LOGIN)}
                        sx={{ 
                          mr: 1,
                          fontWeight: 'medium',
                          '&:hover': {
                            bgcolor: 'rgba(0, 0, 0, 0.04)'
                          }
                        }}
                      >
                        Login
                      </Button>
                      <Button 
                        variant="contained" 
                        color="primary"
                        onClick={() => handleNavigation(APP_ROUTES.REGISTER)}
                        sx={{ 
                          px: 2,
                          boxShadow: '0 4px 14px 0 rgba(0, 118, 255, 0.39)',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            boxShadow: '0 6px 20px rgba(0, 118, 255, 0.23)'
                          }
                        }}
                      >
                        Sign up
                      </Button>
                    </>
                  )}
                </Box>
              )}
              
              {/* Mobile menu button */}
              {isMobile && (
                <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-end' }}>
                  <IconButton
                    size="large"
                    aria-label="menu"
                    aria-controls="menu-appbar"
                    aria-haspopup="true"
                    onClick={() => setDrawerOpen(true)}
                    color="inherit"
                  >
                    <MenuIcon />
                  </IconButton>
                </Box>
              )}
            </Toolbar>
          </Container>
        </AppBar>
      </Slide>
      
      {/* Mobile drawer */}
      <MobileDrawer />
    </>
  );
};

export default MuiNavbar; 